import { CloseOutlined, GlobalOutlined } from '@ant-design/icons'
import { useDebounce, useSet } from 'ahooks'
import { Button, Spin } from 'antd'
import React from 'react'

import { CreatePostLinkInput, useSearchUrlMetaDataQuery } from '@/generated/graphql'

import s from './post-editor.module.less'
import { PostEditorSetState, PostEditorState, emptyPostEditorState } from './post-editor.state'

interface PostEditorLinksProps {
  links: PostEditorState['links']
  content: string
  setState: PostEditorSetState
}

interface PostEditorLinkLoaderProps {
  url: string
  setState: PostEditorSetState
}

interface PostEditorLinkProps {
  link: CreatePostLinkInput
  setState: PostEditorSetState
}

const PostEditorLinkLoader: React.FC<PostEditorLinkLoaderProps> = ({ url, setState }) => {
  React.useEffect(() => {
    setState((state) => {
      state!.links[url] = {
        title: '',
        description: '',
        logo: '',
        url,
        loading: true,
      }
      return state
        ? {
            ...state,
          }
        : emptyPostEditorState
    })
  }, [])

  useSearchUrlMetaDataQuery({
    fetchPolicy: 'no-cache',
    variables: {
      url,
    },
    onCompleted(data) {
      setState((state) => {
        const { title, description, logo, url } = data.search.urlMetaData
        state!.links[url] = {
          title: title || '',
          description: description || '',
          logo: logo || '',
          url: url || '',
        }
        return state
          ? {
              ...state,
            }
          : emptyPostEditorState
      })
    },
  })
  return null
}

const PostEditorLink: React.FC<PostEditorLinkProps> = ({ link, setState }) => {
  const onDelete = () => {
    setState((state) => {
      delete state!.links[link.url]
      return state
        ? {
            ...state,
          }
        : emptyPostEditorState
    })
  }

  if (link['loading']) {
    return (
      <div className={s.linkContainer}>
        <div className={s.linkSpinContainer}>
          <Spin />
        </div>
      </div>
    )
  }

  return (
    <div className={s.linkContainer}>
      <div className={s.linkHeader}>
        <div className={s.linkAvatar}>
          {link.logo ? <img width='16' height='16' src={link.logo} /> : <GlobalOutlined />}
        </div>
        <div className={s.linkTitle}>{link.title || link.url}</div>
        <Button className={s.closeButton} size='small' shape='circle' icon={<CloseOutlined />} onClick={onDelete} />
      </div>
      {link.description && <div className={s.linkContent}>{link.description}</div>}
    </div>
  )
}

const getUrls = (s: string) => {
  const regex = /(http|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?/g
  return [...s['matchAll'](regex)].map((m) => m[0])
}

export const PostEditorLinks: React.FC<PostEditorLinksProps> = ({ content, links, setState }) => {
  const debouncedContent = useDebounce(content, { wait: 1500 })

  const [urlSet, { add }] = useSet<string>()

  React.useEffect(() => {
    getUrls(debouncedContent).forEach(add)
  }, [debouncedContent])

  const loaders = Array.from(urlSet).map((url) => <PostEditorLinkLoader key={url} url={url} setState={setState} />)

  const blocks = Object.values(links).map((link) => <PostEditorLink key={link.url} link={link} setState={setState} />)

  return (
    <>
      {loaders}
      {blocks.length > 0 && <div className={s.linksContainer}>{blocks}</div>}
    </>
  )
}
